/**
 * InlineSubtaskList Component
 * Inline expandable subtask list for task cards
 */
import React, { useState, useCallback } from 'react';
import {
  Button,
  Box,
  Collapse,
  Divider
} from '@mui/material';
import {
  AccountTree,
  KeyboardArrowDown
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { fetchSubtasks } from '../../store/ticketSlice';
import { COLORS } from '../../shared/colors';
import NestedSubtask from './NestedSubtask';

const InlineSubtaskList = ({ task }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localSubtasks, setLocalSubtasks] = useState([]);
  const subtaskCount = task.sub_task_count || 0;


  const handleToggle = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isExpanded && localSubtasks.length === 0) {
      setIsLoading(true);
      try {
        const result = await dispatch(fetchSubtasks(task.id)).unwrap();
        setLocalSubtasks(result);
      } catch (error) {
        console.error('Failed to fetch subtasks:', error);
      } finally {
        setIsLoading(false);
      }
    }
    setIsExpanded(!isExpanded);
  }, [dispatch, task.id, isExpanded, localSubtasks.length]);

  if (subtaskCount === 0) return null;

  return (
    <>
      <Button
        size="small"
        onClick={handleToggle}
        startIcon={<AccountTree sx={{ fontSize: 12 }} />}
        endIcon={
          <KeyboardArrowDown 
            sx={{ 
              fontSize: 12, 
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} 
          />
        }
        disabled={isLoading}
        sx={{
          fontSize: '10px',
          fontWeight: 500,
          color: COLORS.gray[600],
          bgcolor: COLORS.gray[50],
          border: `1px solid ${COLORS.gray[200]}`,
          borderRadius: 1,
          textTransform: 'none',
          minHeight: 'auto',
          py: 0.5,
          px: 1,
          '&:hover': {
            bgcolor: COLORS.gray[100]
          }
        }}
      >
        {isLoading ? 'Loading...' : `${subtaskCount} Subtasks`}
      </Button>

      <Collapse in={isExpanded} timeout={300}>
        <Box sx={{ mt: 1, bgcolor: COLORS.white, borderRadius: 1, p: 1 }}>
          <Divider sx={{ mb: 1, bgcolor: COLORS.gray[200] }} />
          <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
            {localSubtasks.map((subtask) => (
              <NestedSubtask 
                key={subtask.id} 
                subtask={subtask} 
                level={0}
                variant="list"
              />
            ))}
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

export default InlineSubtaskList;